import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { FiUser, FiLock, FiBell, FiSave, FiSettings, FiCreditCard, FiArrowUp, FiXCircle, FiGlobe } from 'react-icons/fi';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const Settings = () => {
  const { user, loadUser } = useAuth();
  const [tab, setTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [wsForm, setWsForm] = useState({ name: '', description: '', type: 'state', country: 'United States', state: '', city: '', regions: '', departments: '' });
  const [wsSaving, setWsSaving] = useState(false);
  const [wsId, setWsId] = useState(null);
  const [profile, setProfile] = useState({
    firstName: user?.firstName || '', lastName: user?.lastName || '',
    phone: user?.phone || '', department: user?.department || '', regionLevel: user?.regionLevel || ''
  });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [notifPrefs, setNotifPrefs] = useState({
    email: user?.notifications?.email !== false,
    inApp: user?.notifications?.inApp !== false,
    digest: user?.notifications?.digest || 'daily',
    alerts: user?.notifications?.alerts !== false,
    reports: user?.notifications?.reports !== false,
  });

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authAPI.updateProfile(profile);
      await loadUser(); // refresh the name shown in the header
      toast.success('Profile updated');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) return toast.error('Passwords do not match');
    if (passwords.newPassword.length < 6) return toast.error('Password must be at least 6 characters');
    setSaving(true);
    try {
      const { data } = await authAPI.changePassword({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      if (data.token) localStorage.setItem('token', data.token); // other devices are signed out; keep this one signed in
      toast.success('Password changed');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    setSaving(false);
  };

  const handleNotifSave = async () => {
    setSaving(true);
    try {
      await authAPI.updateProfile({ notifications: notifPrefs });
      toast.success('Notification preferences saved');
    } catch (err) { toast.error('Failed to save'); }
    setSaving(false);
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isWorkspaceOwner = user?.role === 'admin'; // admins who own a workspace

  // Load workspace data when workspace tab is selected
  useEffect(() => {
    if (tab !== 'workspace' || !isWorkspaceOwner) return;
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/workspaces/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const ws = d.workspace;
        if (ws) {
          setWsId(ws._id);
          setWsForm({
            name: ws.name || '',
            description: ws.description || '',
            type: ws.type || 'state',
            country: ws.country || 'United States',
            state: ws.state || '',
            city: ws.city || '',
            regions: (ws.regions || []).join(', '),
            departments: (ws.departments || []).join(', '),
          });
        }
      })
      .catch(() => {});
  }, [tab, isWorkspaceOwner]);

  const handleWorkspaceSave = async (e) => {
    e.preventDefault();
    if (!wsId) return toast.error('No workspace found');
    setWsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const body = {
        ...wsForm,
        regions: wsForm.regions.split(',').map(r => r.trim()).filter(Boolean),
        departments: wsForm.departments.split(',').map(d => d.trim()).filter(Boolean),
      };
      const res = await fetch(`${API_BASE}/workspaces/${wsId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      toast.success('Workspace updated');
      await loadUser(); // refresh workspace name in header
    } catch (err) { toast.error(err.message || 'Failed to update workspace'); }
    setWsSaving(false);
  };

  // Read ?tab= from URL on mount to set initial tab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTab = params.get('tab');
    const checkoutStatus = params.get('checkout');
    const sessionId = params.get('session_id');

    if (urlTab && urlTab !== 'profile') setTab(urlTab);

    if (checkoutStatus === 'success') {
      setTab('subscription');
      setSubLoading(true);
      const token = localStorage.getItem('token');

      const verifyAndLoad = async () => {
        try {
          let verifiedData = null;

          // Call verify-session to sync Stripe payment → DB
          if (sessionId) {
            const vRes = await fetch(`${API_BASE}/stripe/verify-session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ sessionId }),
            });
            if (vRes.ok) {
              verifiedData = await vRes.json();
            } else {
              const err = await vRes.json().catch(() => ({}));
              console.error('verify-session error:', err);
            }
          }

          // Reload user JWT context (role/workspace may have changed to admin)
          await loadUser();

          // Always fetch from DB — verify has already updated the workspace, this is the source of truth
          const r = await fetch(`${API_BASE}/stripe/subscription`, { headers: { Authorization: `Bearer ${token}` } });
          const d = await r.json();
          setSubscription(d);

          const planName = (d.plan && d.plan !== 'free') ? d.plan : (verifiedData?.plan || params.get('plan') || 'paid');
          toast.success('Subscription activated! Welcome to the ' + planName.charAt(0).toUpperCase() + planName.slice(1) + ' plan.');
        } catch (e) {
          console.error('Checkout verify error:', e);
          toast.error('Payment received but plan activation failed. Please refresh or contact support.');
          setSubscription({ plan: 'free', status: 'none' });
        } finally {
          setSubLoading(false);
          const url = new URL(window.location.href);
          ['checkout', 'plan', 'session_id'].forEach(p => url.searchParams.delete(p));
          window.history.replaceState({}, '', url.toString());
        }
      };

      verifyAndLoad();
    }
  }, []);

  useEffect(() => {
    if (tab !== 'subscription') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') return; // handled by mount effect
    setSubLoading(true);
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/stripe/subscription`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setSubscription(d))
      .catch(() => setSubscription({ plan: 'free', status: 'none' }))
      .finally(() => setSubLoading(false));
  }, [tab]);

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/stripe/portal`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.error(data.message || 'Could not open billing portal');
    } catch { toast.error('Failed to open billing portal'); }
    setPortalLoading(false);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: FiUser },
    { id: 'password', label: 'Password', icon: FiLock },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'subscription', label: 'Subscription', icon: FiCreditCard },
    ...(isWorkspaceOwner ? [{ id: 'workspace', label: 'My Workspace', icon: FiGlobe }] : []),
    ...(isAdmin ? [{ id: 'system', label: 'System', icon: FiSettings }] : []),
  ];

  const roleLabels = { super_admin: 'Super Admin', admin: 'Admin', analyst: 'Analyst', field_worker: 'Field Worker', viewer: 'Viewer' };
  const roleColors = { super_admin: '#dc2626', admin: '#7c3aed', analyst: '#2563eb', field_worker: '#059669', viewer: '#6b7280' };

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>Settings</h1>
        <p style={{ color: '#64748b', marginTop: 4 }}>Manage your profile, security, and preferences</p>
      </div>

      <div className="settings-layout" style={{ display: 'flex', gap: 24 }}>
        {/* Tab Nav */}
        <div className="settings-sidebar" style={{ width: 220, flexShrink: 0 }}>
          <div className="settings-tabs" style={{ background: '#fff', borderRadius: 16, padding: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none', background: tab === t.id ? '#eff6ff' : 'transparent', color: tab === t.id ? '#3b82f6' : '#64748b', fontWeight: tab === t.id ? 600 : 400, fontSize: 14, cursor: 'pointer', textAlign: 'left', marginBottom: 2 }}>
                  <Icon size={18} /> {t.label}
                </button>
              );
            })}
          </div>

          {/* User Card */}
          <div className="settings-usercard" style={{ background: '#fff', borderRadius: 16, padding: 20, marginTop: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: `${roleColors[user?.role]}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', fontWeight: 800, fontSize: 22, color: roleColors[user?.role] }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{user?.firstName} {user?.lastName}</div>
            <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>{user?.email}</div>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${roleColors[user?.role]}15`, color: roleColors[user?.role], marginTop: 8 }}>
              {roleLabels[user?.role]}
            </span>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          {tab === 'profile' && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>Profile Information</h2>
              <form onSubmit={handleProfileSave}>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div><label style={labelStyle}>First Name</label><input value={profile.firstName} onChange={e => setProfile({ ...profile, firstName: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Last Name</label><input value={profile.lastName} onChange={e => setProfile({ ...profile, lastName: e.target.value })} style={inputStyle} /></div>
                </div>
                <div style={{ marginBottom: 16 }}><label style={labelStyle}>Email</label><input value={user?.email} disabled style={{ ...inputStyle, background: '#f8fafc', color: '#94a3b8' }} /></div>
                <div style={{ marginBottom: 16 }}><label style={labelStyle}>Phone</label><input value={profile.phone} onChange={e => setProfile({ ...profile, phone: e.target.value })} style={inputStyle} /></div>
                <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                  <div><label style={labelStyle}>Department</label><input value={profile.department} onChange={e => setProfile({ ...profile, department: e.target.value })} style={inputStyle} /></div>
                  <div><label style={labelStyle}>Region Level</label>
                    <select value={profile.regionLevel} onChange={e => setProfile({ ...profile, regionLevel: e.target.value })} style={inputStyle}>
                      <option value="">Select Level</option>
                      <option value="international">International</option>
                      <option value="national">National</option>
                      <option value="state">State</option>
                      <option value="local">Local</option>
                    </select>
                  </div>
                </div>
                <button type="submit" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>
                  <FiSave size={16} /> {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          )}

          {tab === 'password' && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>Change Password</h2>
              <form onSubmit={handlePasswordChange} style={{ maxWidth: 400 }}>
                <div style={{ marginBottom: 16 }}><label style={labelStyle}>Current Password</label><input type="password" value={passwords.currentPassword} onChange={e => setPasswords({ ...passwords, currentPassword: e.target.value })} required style={inputStyle} /></div>
                <div style={{ marginBottom: 16 }}><label style={labelStyle}>New Password</label><input type="password" value={passwords.newPassword} onChange={e => setPasswords({ ...passwords, newPassword: e.target.value })} required minLength={6} style={inputStyle} /></div>
                <div style={{ marginBottom: 20 }}><label style={labelStyle}>Confirm New Password</label><input type="password" value={passwords.confirmPassword} onChange={e => setPasswords({ ...passwords, confirmPassword: e.target.value })} required style={inputStyle} /></div>
                <button type="submit" disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>
                  <FiLock size={16} /> {saving ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {tab === 'notifications' && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>Notification Preferences</h2>
              <div style={{ display: 'grid', gap: 16, maxWidth: 500 }}>
                {[
                  { key: 'email', label: 'Email Notifications', desc: 'Receive alerts via email' },
                  { key: 'inApp', label: 'In-App Notifications', desc: 'Show notifications in the app' },
                  { key: 'alerts', label: 'Disease Outbreak Alerts', desc: 'Critical health event alerts' },
                  { key: 'reports', label: 'Report Notifications', desc: 'Get notified when reports are ready' },
                ].map(pref => (
                  <div key={pref.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: '#f8fafc', borderRadius: 10 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{pref.label}</div>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{pref.desc}</div>
                    </div>
                    <label style={{ position: 'relative', width: 48, height: 26, cursor: 'pointer' }}>
                      <input type="checkbox" checked={notifPrefs[pref.key]} onChange={e => setNotifPrefs({ ...notifPrefs, [pref.key]: e.target.checked })} style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: 'absolute', inset: 0, background: notifPrefs[pref.key] ? '#3b82f6' : '#cbd5e1', borderRadius: 13, transition: '0.3s' }}>
                        <span style={{ position: 'absolute', left: notifPrefs[pref.key] ? 24 : 3, top: 3, width: 20, height: 20, background: '#fff', borderRadius: '50%', transition: '0.3s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </span>
                    </label>
                  </div>
                ))}
                <div style={{ padding: '14px 18px', background: '#f8fafc', borderRadius: 10 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Email Digest Frequency</div>
                  <select value={notifPrefs.digest} onChange={e => setNotifPrefs({ ...notifPrefs, digest: e.target.value })} style={inputStyle}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="none">None</option>
                  </select>
                </div>
              </div>
              <button onClick={handleNotifSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer', marginTop: 20 }}>
                <FiSave size={16} /> {saving ? 'Saving...' : 'Save Preferences'}
              </button>
            </div>
          )}

          {tab === 'subscription' && (
            <div>
              {/* Current Plan Card */}
              <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: 20 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10 }}><FiCreditCard size={20} color="#3b82f6" /> Subscription & Plan</h2>
                {subLoading ? (
                  <div style={{ color: '#64748b', padding: '20px 0' }}>Loading subscription info...</div>
                ) : (
                  <>
                    <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                      {[
                        { label: 'Current Plan', value: subscription ? (subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)) : (user?.role === 'viewer' ? 'Free (Viewer)' : 'Free') },
                        { label: 'Status', value: subscription?.status === 'active' ? '✅ Active' : subscription?.status === 'trialing' ? '🔄 Trial' : subscription?.status === 'past_due' ? '⚠️ Past Due' : subscription?.status === 'canceled' ? '❌ Canceled' : '— Free Access' },
                        { label: 'Max Users', value: subscription?.maxUsers ? `${subscription.maxUsers} users` : '1 user (Viewer)' },
                        { label: 'Next Renewal', value: subscription?.renewalDate ? new Date(subscription.renewalDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '—' },
                      ].map((item, i) => (
                        <div key={i} style={{ padding: '16px 20px', background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                          <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.label}</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>{item.value}</div>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                      {['active', 'trialing'].includes(subscription?.status) && subscription?.stripeManaged && (
                        <button onClick={handlePortal} disabled={portalLoading}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                          <FiCreditCard size={16} /> {portalLoading ? 'Opening...' : 'Manage Subscription'}
                        </button>
                      )}
                      {['active', 'trialing'].includes(subscription?.status) && !subscription?.stripeManaged && (
                        <div style={{ padding: '11px 18px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, fontSize: 13, color: '#166534', fontWeight: 600 }}>
                          ✅ Plan managed by administrator
                        </div>
                      )}
                      {(!subscription?.plan || subscription?.plan === 'free' || !['active', 'trialing'].includes(subscription?.status)) && (
                        <a href="/pricing"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: '#00d4aa', color: '#000', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', textDecoration: 'none' }}>
                          <FiArrowUp size={16} /> Upgrade Plan
                        </a>
                      )}
                      {subscription?.status === 'active' && subscription?.stripeManaged && (
                        <button onClick={handlePortal} disabled={portalLoading}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', background: 'transparent', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                          <FiXCircle size={16} /> Cancel Subscription
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* No Refund Policy */}
              <div style={{ background: '#fffbeb', borderRadius: 12, padding: '16px 20px', border: '1px solid #fde68a' }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#92400e', marginBottom: 6 }}>⚠️ Refund Policy</div>
                <p style={{ fontSize: 13, color: '#78350f', lineHeight: 1.6, margin: 0 }}>
                  All subscription payments are <strong>non-refundable</strong>. You may cancel at any time — your access will continue until the end of the current billing period. No partial refunds are issued for unused time. By subscribing, you agree to this no-refund policy.
                </p>
              </div>
            </div>
          )}

          {tab === 'workspace' && isWorkspaceOwner && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}><FiGlobe size={20} color="#3b82f6" /> My Workspace</h2>
              <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>Configure your workspace details, regions, and departments.</p>
              {!wsId ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
                  No workspace found. Upgrade your plan to get your own workspace.
                </div>
              ) : (
                <form onSubmit={handleWorkspaceSave}>
                  <div className="form-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Workspace Name</label><input value={wsForm.name} onChange={e => setWsForm({ ...wsForm, name: e.target.value })} required style={inputStyle} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Description</label><textarea value={wsForm.description} onChange={e => setWsForm({ ...wsForm, description: e.target.value })} rows={2} style={{ ...inputStyle, resize: 'vertical' }} /></div>
                    <div>
                      <label style={labelStyle}>Type / Level</label>
                      <select value={wsForm.type} onChange={e => setWsForm({ ...wsForm, type: e.target.value })} style={inputStyle}>
                        {['international', 'national', 'state', 'county', 'city', 'local'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                      </select>
                    </div>
                    <div><label style={labelStyle}>Country</label><input value={wsForm.country} onChange={e => setWsForm({ ...wsForm, country: e.target.value })} style={inputStyle} /></div>
                    <div><label style={labelStyle}>State / Province</label><input value={wsForm.state} onChange={e => setWsForm({ ...wsForm, state: e.target.value })} placeholder="e.g., Florida" style={inputStyle} /></div>
                    <div><label style={labelStyle}>City</label><input value={wsForm.city} onChange={e => setWsForm({ ...wsForm, city: e.target.value })} placeholder="e.g., Miami" style={inputStyle} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Regions <span style={{ fontWeight: 400, color: '#94a3b8' }}>(comma-separated)</span></label><input value={wsForm.regions} onChange={e => setWsForm({ ...wsForm, regions: e.target.value })} placeholder="e.g., North, South, Central" style={inputStyle} /></div>
                    <div style={{ gridColumn: '1/-1' }}><label style={labelStyle}>Departments <span style={{ fontWeight: 400, color: '#94a3b8' }}>(comma-separated)</span></label><input value={wsForm.departments} onChange={e => setWsForm({ ...wsForm, departments: e.target.value })} placeholder="e.g., Administration, Epidemiology, Community Health" style={inputStyle} /></div>
                  </div>
                  <button type="submit" disabled={wsSaving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 600, cursor: 'pointer' }}>
                    <FiSave size={16} /> {wsSaving ? 'Saving...' : 'Save Workspace'}
                  </button>
                </form>
              )}
            </div>
          )}

          {tab === 'system' && (
            <div style={{ background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 24 }}>System Information</h2>
              <div style={{ display: 'grid', gap: 12 }}>
                {[
                  { label: 'Platform Version', value: '1.0.0' },
                  { label: 'Database', value: 'MongoDB Atlas' },
                  { label: 'Email Service', value: 'Hostinger SMTP' },
                  { label: 'AI Engine', value: 'OpenAI GPT-4' },
                  { label: 'Frontend', value: 'React 18' },
                  { label: 'Backend', value: 'Node.js / Express' },
                  { label: 'Authentication', value: 'JWT' },
                  { label: 'Data Encryption', value: 'bcrypt (passwords), HTTPS (transit)' },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: i % 2 === 0 ? '#f8fafc' : '#fff', borderRadius: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: '#374151' }}>{item.label}</span>
                    <span style={{ fontSize: 14, color: '#64748b' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };

export default Settings;
